/**
 * The four-eyes HAND-OFF, encoded.
 *
 * doc 03 §7.4's second-actor clause is a rule about two PRINCIPALS, and the only way a browser can
 * honour it is for the second principal to authenticate in their OWN session. So the flow this
 * application implements has no step in which the initiator holds anything belonging to the
 * approver:
 *
 *   1. the initiator's screen states the act — action, aggregate, the version it read, and the
 *      complete proposed post-state where the act carries one — and encodes it as a REQUEST token;
 *   2. the approver, signed in as themselves in their own session (a different browser profile, a
 *      different machine, whatever their organization requires), pastes it into the approvals desk.
 *      They see the same facts, name the initiator, step up for that exact action, and
 *      `POST /v1/approvals` records THEIR decision under THEIR session;
 *   3. the server's `Approval` — which carries `approver_user_id` — is encoded as a GRANT token and
 *      handed back to the initiator, who pastes it beside the operation.
 *
 * The tokens are not credentials and carry no secret: `iam.Approval`'s own comment says "the
 * binding IS the credential, so there is nothing to hand over and nothing to steal in transit".
 * They exist so that neither side has to retype a UUID, and so the initiator's screen can show
 * exactly which approval it is about to spend.
 *
 * WHY THAT IS SAFE EVEN THOUGH THE TOKEN IS UNSIGNED: the backend never authorises from anything in
 * it. `catalogue.ActivateRelease`, `RollbackRelease`, `Takedown`, `tenancy.SetRecordingPolicy` and
 * `SetPublicationState` each call `ConsumeApprovalTx` INSIDE their own transaction, matching on
 * (action, aggregate type, aggregate id, expected version, binding digest, initiator) and taking
 * the approver from the row they consumed. A forged token buys nothing an empty one does not: the
 * transaction rolls back. The token's job is to carry FACTS between two screens, and this module's
 * job is to make sure the facts on the two screens are the same ones.
 *
 * WHAT THIS REPLACES: a free-text `approver_user_id` box in the initiator's own session. That box
 * asked one person to name another and called it four eyes.
 */

const PREFIX_REQUEST = 'MQ1R.';
const PREFIX_GRANT = 'MQ1A.';

export interface ApprovalProposal {
  role_codes: string[];
  participation_ids?: string[];
  expo_ids?: string[];
}

/**
 * The takedown proposal (`aggregate_type: 'takedown'`).
 *
 * `POST /v1/approvals` renders `catalogue.TakedownDigest(subject_type, subject_id, aggregate_id,
 * reason_code)` from these three fields plus the aggregate, and `catalogue.Takedown` re-renders the
 * identical digest when it spends the approval. So the approver is bound to WHICH content is being
 * removed, not merely to the expo it lives in.
 */
export interface TakedownProposal {
  subject_type: string;
  subject_id: string;
  reason_code: string;
}

/**
 * The release proposal (`aggregate_type: 'release_activation' | 'release_rollback'`).
 *
 * ONE FIELD, because the server renders the other four. `handleCreateApproval` reads the release
 * row and digests (release_id, session_id, channel, locale, tier); an approver that could send
 * those could bind an approval to an identity no release actually has.
 */
export interface ReleaseProposal {
  release_id: string;
}

/**
 * The exception-resolution proposal (`aggregate_type: 'attribution_exception'`).
 *
 * `POST /v1/approvals` renders `crm.ExceptionResolutionDigest(aggregate_id, decision, new brokerage,
 * new broker, expo, sorted deal ids)` from these fields plus the aggregate, and
 * `crm.ResolveException` re-renders the identical digest when it spends the approval. So the second
 * principal is bound to the OUTCOME and to the ownership and credit move it carries — not merely to
 * "this exception, at this version", which would cover a rejection and a correction to any
 * brokerage, any broker and any deal's commercial credit alike.
 *
 * THE SUMMARY IS DELIBERATELY ABSENT, exactly as it is from the server's digest: it is the
 * resolver's written reasoning, and binding an approval to prose would make a typo correction need a
 * fresh second-person decision while adding no control.
 */
export interface ExceptionProposal {
  decision: string;
  new_brokerage_org_id?: string;
  new_broker_user_id?: string;
  expo_id?: string;
  deal_ids?: string[];
}

/**
 * What the initiator asks a second principal to approve.
 *
 * TWO KINDS, because the backend has two surfaces that RECORD a second principal's decision, and
 * they are not interchangeable:
 *
 *   - `aggregate` is `POST /v1/approvals` — an act applied to one VERSION of one aggregate. The
 *     act consumes the row it creates, transactionally;
 *   - `identity` is `POST /v1/identity-approvals`, doc 03 §7.10's own half. A merge is bound to the
 *     two anchors rather than to a row version, and `identities:merge` spends an approval created
 *     there and nowhere else. The screen that used to raise a `person` approval at `/v1/approvals`
 *     for a merge was producing a grant the merge could never spend.
 *
 * `attribution.exception.resolve` IS AN `aggregate` ACT NOW, AND USED NOT TO BE (BE-3). It took an
 * `approver_user_id` in the INITIATOR's own body and checked only that the named principal WOULD
 * hold the permission — eligibility, not a decision — so there was no approval row to hand over and
 * this module said so. The backend gave the act a real aggregate (`attribution_exception`, the
 * EXCEPTION at its own version) with a proposal of its own, and `crm.ResolveException` consumes the
 * row inside its transaction. So the screen raises a genuine request here like every other
 * four-eyes act; see `AdminExceptions`.
 */
export type HandoffKind = 'aggregate' | 'identity';

export interface HandoffRequest {
  kind?: HandoffKind;
  action: string;
  /** `aggregate` only. */
  aggregateType?: string;
  aggregateId?: string;
  expectedVersion?: number;
  /** `identity` only: the two anchors the merge is bound to. */
  survivorPersonId?: string;
  absorbedPersonId?: string;
  reasonCode: string;
  note?: string;
  /** The membership proposal — the complete proposed role and scope set. */
  proposal?: ApprovalProposal;
  /** The takedown proposal — which content is removed, and under what reason. */
  takedown?: TakedownProposal;
  /** The release proposal — which artifact becomes (or stops being) the public pointer. */
  release?: ReleaseProposal;
  /** The exception proposal — the outcome, and the ownership and credit move it carries. */
  exception?: ExceptionProposal;
  /** What the initiator's screen is about to do, in words, for the approver to read. */
  label?: string;
}

/** The approval the approver's session produced, as the initiator receives it. */
export interface HandoffGrant {
  kind?: HandoffKind;
  approvalId: string;
  /**
   * INFORMATIONAL, AND NEVER PUT ON A REQUEST. `iam.Approval` names its approver so the initiator's
   * screen can display who decided; the operation itself does not send it, because every route that
   * consumes an approval refuses `approver_user_id` outright (`decodeStrict`) and takes the approver
   * out of the row it consumed. `crm.Approval` names none — the merge spends `approval_id` itself.
   */
  approverUserId?: string;
  action: string;
  aggregateType?: string;
  aggregateId?: string;
  expectedVersion?: number;
  survivorPersonId?: string;
  absorbedPersonId?: string;
  /**
   * THE INTENT DIGEST THE APPROVER ACTUALLY SAW (round-2 H5).
   *
   * A grant used to be checked against the initiator's CURRENT form state, field by field. That is
   * a comparison against something mutable: every field the initiator could still change between
   * pasting the grant and pressing the button was outside it, and `grantMatches` re-ran on each
   * render against whatever the form said at that moment. The digest is computed ONCE from the
   * request the approver decoded and is carried back verbatim, so what the two screens compare is
   * one immutable string standing for the whole act — including the proposal, which the field-wise
   * comparison never looked at at all.
   */
  intent?: string;
  expiresAt?: string;
}

function encode(prefix: string, value: unknown): string {
  const json = JSON.stringify(value);
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return prefix + btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decode(prefix: string, token: string): unknown {
  const trimmed = token.trim();
  if (!trimmed.startsWith(prefix)) throw new Error('handoff: wrong token kind');
  const b64 = trimmed.slice(prefix.length).replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The AGGREGATES `POST /v1/approvals` accepts, from `expoOfApprovalTarget`'s own switch.
 *
 * It is a closed list here because it is a closed list there: an `aggregate_type` outside it is
 * refused naming the ones that are inside, and a screen that raises one is asking an approver to try
 * something that cannot succeed. `person` is deliberately absent — the identity acts have their own
 * surface (`kind: 'identity'`).
 *
 * `attribution_exception` JOINED IT WITH BE-3, and it is EXPO-LESS: `expoOfApprovalTarget` returns
 * no expo for it, because `crm_attribution_exceptions.expo_id` is nullable and, where set, names the
 * expo the exception is ABOUT rather than a scope anybody's authority is measured in.
 * `attribution.exception.resolve` is a Platform Super Admin key, so the approver is checked at
 * platform scope — the same resolution a membership approval gets, reached the same way.
 */
export const APPROVAL_AGGREGATE_TYPES = [
  'participation',
  'expo',
  'membership',
  'takedown',
  'release_activation',
  'release_rollback',
  'attribution_exception',
] as const;

export type ApprovalAggregateType = (typeof APPROVAL_AGGREGATE_TYPES)[number];

/** The aggregates whose act carries a payload, from `iam.approvalProposed`. */
const AGGREGATE_PROPOSAL: Record<string, 'proposal' | 'takedown' | 'release' | 'exception'> = {
  membership: 'proposal',
  takedown: 'takedown',
  release_activation: 'release',
  release_rollback: 'release',
  attribution_exception: 'exception',
};

/** The two outcomes `handleCreateApproval` digests; anything else is a 400 there. */
const EXCEPTION_DECISIONS = ['corrected', 'rejected'];

/**
 * THE STABLE IDENTITY OF ONE ACT, as a single canonical string.
 *
 * It is deliberately readable rather than hashed: both screens display it, and an operator
 * comparing two short strings by eye is a check a cryptographic digest would take away from them
 * for no gain — the string is not a secret and is not a credential, it is a name for the act. What
 * matters is that it is TOTAL (every bound field is in it, in a fixed order) and IMMUTABLE (it is
 * computed once, from the request, and travels with the grant).
 *
 * The fields are exactly the ones the backend binds an approval by: action, aggregate type and id,
 * the expected version, and the proposal — the same tuple `ConsumeApprovalTx` matches on. Reason
 * code and note are NOT in it, because the server does not bind them; including them would make the
 * two screens disagree over text that changes nothing about what is authorised.
 */
export function intentDigest(req: HandoffRequest): string {
  const kind = req.kind ?? 'aggregate';
  const parts = [`v1`, kind, req.action];
  if (kind === 'identity') {
    parts.push((req.survivorPersonId ?? '').toLowerCase(), (req.absorbedPersonId ?? '').toLowerCase());
    return parts.join('|');
  }
  parts.push(req.aggregateType ?? '', (req.aggregateId ?? '').toLowerCase(), String(req.expectedVersion ?? ''));
  if (req.proposal) {
    parts.push(
      'proposal:' +
        [...req.proposal.role_codes].sort().join(',') +
        ';' +
        [...(req.proposal.participation_ids ?? [])].map((s) => s.toLowerCase()).sort().join(',') +
        ';' +
        [...(req.proposal.expo_ids ?? [])].map((s) => s.toLowerCase()).sort().join(','),
    );
  }
  if (req.takedown) {
    parts.push(
      `takedown:${req.takedown.subject_type};${req.takedown.subject_id.toLowerCase()};${req.takedown.reason_code}`,
    );
  }
  if (req.release) {
    parts.push(`release:${req.release.release_id.toLowerCase()}`);
  }
  if (req.exception) {
    // THE DEAL IDS ARE SORTED, because `crm.ExceptionResolutionDigest` sorts them (`sort.Strings`):
    // two operators listing the same deals in different orders are deciding the same thing, and the
    // two screens must agree on one string for it. The empty optionals are rendered as empty rather
    // than omitted, so "no brokerage named" is a value in the digest and not a shorter digest.
    const deals = [...(req.exception.deal_ids ?? [])].map((s) => s.toLowerCase()).sort();
    parts.push(
      'exception:' +
        [
          req.exception.decision,
          (req.exception.new_brokerage_org_id ?? '').toLowerCase(),
          (req.exception.new_broker_user_id ?? '').toLowerCase(),
          (req.exception.expo_id ?? '').toLowerCase(),
          deals.join(','),
        ].join(';'),
    );
  }
  return parts.join('|');
}

export function encodeRequest(req: HandoffRequest): string {
  return encode(PREFIX_REQUEST, req);
}

/**
 * Reads a request token, and refuses anything that is not a complete, well-formed act.
 *
 * `expected_version` in particular must be a real version: `iam.CreateApproval` refuses anything
 * below 1 — "an approval is for one version of one aggregate, so a change made after it was given
 * cannot be carried out under it" — and a screen that sent `0` was asking for a blank cheque and
 * getting a validation error instead of an approval.
 *
 * THE PROPOSAL IS CHECKED AGAINST THE AGGREGATE, exactly as `handleCreateApproval` checks it: the
 * union is exclusive, the aggregates that carry a payload must have theirs, and the ones that do
 * not must not name one. A desk that posted a mismatched pair would be sending a request the server
 * refuses with a 422 the approver cannot act on.
 */
export function decodeRequest(token: string): HandoffRequest {
  const raw = decode(PREFIX_REQUEST, token) as Partial<HandoffRequest>;
  if (!raw || typeof raw !== 'object') throw new Error('handoff: not a request');
  if (!raw.action) throw new Error('handoff: the request names no action');
  const kind = raw.kind ?? 'aggregate';
  if (kind === 'identity') {
    if (!raw.survivorPersonId || !UUID.test(raw.survivorPersonId)) {
      throw new Error('handoff: survivor_person_id is not a uuid');
    }
    if (!raw.absorbedPersonId || !UUID.test(raw.absorbedPersonId)) {
      throw new Error('handoff: absorbed_person_id is not a uuid');
    }
    return raw as HandoffRequest;
  }
  if (!raw.aggregateType) throw new Error('handoff: the request names no aggregate type');
  if (!raw.aggregateId || !UUID.test(raw.aggregateId)) throw new Error('handoff: aggregate_id is not a uuid');
  if (typeof raw.expectedVersion !== 'number' || raw.expectedVersion < 1) {
    throw new Error('handoff: expected_version must be the version the initiator read');
  }
  if (!(APPROVAL_AGGREGATE_TYPES as readonly string[]).includes(raw.aggregateType)) {
    throw new Error(
      `handoff: /v1/approvals accepts ${APPROVAL_AGGREGATE_TYPES.join(', ')}; ` +
        `"${raw.aggregateType}" is not one of them`,
    );
  }
  const members = [raw.proposal, raw.takedown, raw.release, raw.exception].filter(
    (m) => m !== undefined && m !== null,
  );
  if (members.length > 1) {
    throw new Error('handoff: an approval carries ONE proposal');
  }
  const expected = AGGREGATE_PROPOSAL[raw.aggregateType];
  if (expected === 'proposal' && (!raw.proposal || raw.proposal.role_codes.length === 0)) {
    throw new Error('handoff: a membership approval must carry the complete proposed role set');
  }
  if (expected === 'takedown' && (!raw.takedown || !raw.takedown.subject_id || !raw.takedown.subject_type)) {
    throw new Error('handoff: a takedown approval names the content it removes');
  }
  if (expected === 'release' && (!raw.release || !UUID.test(raw.release.release_id))) {
    throw new Error('handoff: a release transition approval names the release it publishes');
  }
  if (expected === 'exception') {
    if (!raw.exception || !raw.exception.decision) {
      throw new Error('handoff: an exception approval names the resolution it authorises');
    }
    // The server digests these two spellings and refuses every other: a token carrying a third
    // would be one the approver's desk could only fail on.
    if (!EXCEPTION_DECISIONS.includes(raw.exception.decision)) {
      throw new Error('handoff: exception.decision is corrected or rejected');
    }
    for (const [field, value] of [
      ['new_brokerage_org_id', raw.exception.new_brokerage_org_id],
      ['new_broker_user_id', raw.exception.new_broker_user_id],
      ['expo_id', raw.exception.expo_id],
      ...(raw.exception.deal_ids ?? []).map((d) => ['deal_ids', d] as const),
    ] as const) {
      if (value !== undefined && value !== '' && !UUID.test(value)) {
        throw new Error(`handoff: exception.${field} must be a uuid`);
      }
    }
  }
  if (expected === undefined && members.length > 0) {
    throw new Error(`handoff: the ${raw.aggregateType} aggregate carries no proposal`);
  }
  return raw as HandoffRequest;
}

export function encodeGrant(grant: HandoffGrant): string {
  return encode(PREFIX_GRANT, grant);
}

export function decodeGrant(token: string): HandoffGrant {
  const raw = decode(PREFIX_GRANT, token) as Partial<HandoffGrant>;
  if (!raw || typeof raw !== 'object') throw new Error('handoff: not a grant');
  if (!raw.approvalId || !UUID.test(raw.approvalId)) throw new Error('handoff: approval_id is not a uuid');
  if (!raw.action) throw new Error('handoff: the grant names no action');
  if (!raw.intent) throw new Error('handoff: the grant carries no intent digest');
  // AN APPROVER ID IS NEVER REQUIRED AND IS NEVER SENT ONWARD. It used to be mandatory here,
  // because the operation used to put it in its own body; every route that consumes an approval now
  // refuses that field outright, so the id is display text and a grant without one is still valid.
  if (raw.approverUserId !== undefined && !UUID.test(raw.approverUserId)) {
    throw new Error('handoff: approver_user_id is not a uuid');
  }
  return raw as HandoffGrant;
}

/**
 * The grant must be for the act in hand.
 *
 * ONE COMPARISON, ON ONE IMMUTABLE STRING (round-2 H5). It used to be a field-by-field comparison
 * against the initiator's live form state, which meant the answer could change under the operator's
 * feet and never covered the proposal at all. Now the approver's digest — computed from the request
 * they decoded — is compared with the digest of the request in hand, and a grant raised for a
 * different act, a different object, a different version or a different PROPOSAL is refused here
 * rather than sent.
 */
export function grantMatches(grant: HandoffGrant, req: HandoffRequest): boolean {
  if (!grant.intent) return false;
  return grant.intent === intentDigest(req);
}
